import { Link } from 'react-router-dom'
import type { RecurringItem } from '@/api/recurring'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { ROUTES } from '@/routes/routes'
import { ReminderRow } from './ReminderRow'

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Reminders due soon: late ones first, then the next two weeks, grouped by date. */
export function ComingUp({
  groups,
  today,
  failed = false,
  onRetry,
}: {
  groups: { date: string; items: RecurringItem[] }[]
  today: string
  failed?: boolean
  onRetry?: () => void
}) {
  const { formatWhole } = useFormatCurrency()
  // What's due to go out; expected income isn't a bill.
  const total = groups
    .flatMap((g) => g.items)
    .filter((i) => i.type === 'EXPENSE')
    .reduce((s, i) => s + i.amount, 0)
  return (
    <section aria-label="Coming up">
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--fl-text-2)]">
          Coming up
        </h2>
        {groups.length > 0 && (
          <span className="text-xs tabular-nums text-[var(--fl-text-2)]">
            {formatWhole(total)} due
          </span>
        )}
      </div>
      <div className="rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 py-1 shadow-[var(--card-shadow-sm)]">
        {failed ? (
          <div className="py-4 text-sm">
            <p className="text-[var(--fl-error)]">Couldn’t load your reminders.</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-1 text-[var(--fl-primary)] underline"
              >
                Try again
              </button>
            )}
          </div>
        ) : groups.length === 0 ? (
          <p className="py-4 text-sm text-[var(--fl-text-2)]">Nothing due in the next two weeks.</p>
        ) : (
          groups.map((g) => {
            const [y, m, d] = g.date.split('-').map(Number)
            const date = new Date(y, m - 1, d)
            return (
              <div
                key={g.date}
                className="flex gap-3 border-t border-[var(--fl-divider)] py-1 first:border-t-0"
              >
                <div className="w-11 shrink-0 pt-2 text-center">
                  <b className="block font-serif text-xl leading-none">{d}</b>
                  <small className="text-[10.5px] uppercase tracking-wide text-[var(--fl-text-2)]">
                    {g.date === today
                      ? 'Today'
                      : date.getMonth() === Number(today.slice(5, 7)) - 1
                        ? WEEKDAY[date.getDay()]
                        : MONTH[date.getMonth()]}
                  </small>
                </div>
                <ul className="min-w-0 flex-1">
                  {g.items.map((i) => (
                    <ReminderRow key={`${g.date}-${i.id}`} item={i} date={g.date} today={today} />
                  ))}
                </ul>
              </div>
            )
          })
        )}
        <div className="border-t border-[var(--fl-divider)] py-2 text-right">
          <Link
            to={ROUTES.APP.RECURRING}
            className="text-[12.5px] text-[var(--fl-primary)] hover:underline"
          >
            All recurring ›
          </Link>
        </div>
      </div>
    </section>
  )
}
